import { UploadSimple } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { fileMatchesAccept } from "../hooks/useFileDrop";

/** Returns true when the drag event carries external OS files (not internal DOM drags). */
const isExternalFileDrag = (e: React.DragEvent) =>
  Array.from(e.dataTransfer?.types ?? []).includes("Files");

interface AskPunttDropZoneProps {
  /** Called with dropped files. Mirrors clicking the "+" (InputPlusMenu) button. */
  onFilesDropped: (files: File[]) => void;
  /** When true, dropped files are ignored and no overlay is shown. */
  disabled?: boolean;
  /**
   * File patterns (as `<input accept>` takes them) a drop may hand over.
   * Unset accepts every dropped file, as before; set, files matching none of
   * the patterns are dropped on the floor rather than attached.
   */
  accept?: string[];
  /** Extra classes for the wrapping element. */
  className?: string;
  /** Forwarded to the wrapping element (e.g. for E2E selectors). */
  "data-testid"?: string;
  /** Content to wrap. */
  children: ReactNode;
}

/**
 * Scoped drag-and-drop file upload overlay for the Ask Puntt home and chat
 * views. Listens to drag events on its wrapping element and shows a visual
 * "drop files to attach" overlay while an external file drag is in progress.
 *
 * The overlay is visually inspired by the tickets view's
 * `DragAndDropFileUpload`, but scoped to this component's container rather
 * than the full viewport, so the rest of the app (e.g. sidebar) stays intact.
 */
export function AskPunttDropZone({
  onFilesDropped,
  disabled,
  accept,
  className,
  children,
  "data-testid": dataTestId = "ask-puntt-drop-zone",
}: AskPunttDropZoneProps) {
  const { t } = useTranslation("ui");
  const [isDragging, setIsDragging] = useState(false);
  // DragEnter / DragLeave fire for every child element, so we use a counter
  // to only clear the overlay once the drag has left the wrapping element.
  const dragDepth = useRef(0);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      if (!isExternalFileDrag(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      if (dragDepth.current === 1) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      if (!isExternalFileDrag(e)) return;
      // Required to allow drop.
      e.preventDefault();
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      if (!isExternalFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []).filter(
        (file) => !accept || fileMatchesAccept(file, accept),
      );
      if (files.length > 0) {
        onFilesDropped(files);
      }
    },
    [disabled, accept, onFilesDropped],
  );

  return (
    <div
      className={`relative ${className ?? ""}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={dataTestId}
    >
      {children}
      {isDragging && (
        <div
          className="pointer-events-none absolute inset-0 z-50 flex items-end justify-center bg-gradient-to-t from-puntt-accent-5/80 via-puntt-accent-3/50 to-transparent backdrop-blur-sm transition-all"
          style={{
            maskImage: "linear-gradient(to top, black 50%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to top, black 50%, transparent 100%)",
          }}
          data-testid="ask-puntt-drop-overlay"
        >
          <div className="m-6 flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-puntt-accent-9 px-8 py-6 text-puntt-accent-11">
            <UploadSimple size={32} />
            <Text
              size="5"
              weight="medium"
              style={{
                textShadow:
                  "0 0 5px rgba(111, 111, 223, 0.5), 0 0 10px rgba(111, 111, 223, 0.4)",
              }}
            >
              {t("dropZone.dropFilesToAttach")}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
